import React from 'react'
import loadable from '@loadable/component'

/* Debug mode toggle */
const isDebugMode = false

/* Pages */
import WebContentPage from '../pages/WebContentPage'
import GDIContentPage from '../pages/GDIContentPage'

/* Visible warning for unrecognized/missing block types — preview/dev only, null in prod. */
import UnsupportedContentFallback from './unsupportedContentFallback'

/* GDI Assets — directly-renderable primitives.
 * GDILinkBlock is intentionally NOT in this map; it's used internally by other GDI components
 * to wrap content in an anchor and isn't dropped into content areas standalone.
 *
 * The remaining Layouts (GDIHeroSliderBlock) and other primitives referenced in the query
 * but without a template yet fall through to UnsupportedContentFallback.
 */
const GDIImageBlock = loadable(() => import('../gdi/assets/gdiImageBlock'), {ssr: true})
const GDIVideoBlock = loadable(() => import('../gdi/assets/gdiVideoBlock'), {ssr: true})
const GDITextBlock = loadable(() => import('../gdi/assets/gdiTextBlock'), {ssr: true})
const GDICtaBlock = loadable(() => import('../gdi/assets/gdiCtaBlock'), {ssr: true})
const GDIProductTileBlock = loadable(() => import('../gdi/assets/gdiProductTileBlock'), {ssr: true})
const GDICarouselGridBlock = loadable(() => import('../gdi/layouts/gdiCarouselGridBlock'), {
    ssr: true
})
const GDIContentTileBlock = loadable(() => import('../gdi/layouts/gdiContentTileBlock'), {
    ssr: true
})
const GDIHeroSliderBlock = loadable(() => import('../gdi/layouts/gdiHeroSliderBlock'), {
    ssr: true
})
const GDITableLayoutBlock = loadable(() => import('../gdi/layouts/gdiTableLayoutBlock'), {
    ssr: true
})
const GDITabsLayoutBlock = loadable(() => import('../gdi/layouts/gdiTabsLayoutBlock'), {
    ssr: true
})
const GDICollapsibleItemBlock = loadable(() => import('../gdi/assets/gdiCollapsibleItemBlock'), {
    ssr: true
})
const GDIContentAssetBlock = loadable(() => import('../gdi/assets/gdiContentAssetBlock'), {
    ssr: true
})
const GDIRewardStatusSectionBlock = loadable(() => import('../gdi/layouts/gdiRewardStatusSectionBlock'), {
    ssr: true
})
const GDIRewardTileBlock = loadable(() => import('../gdi/layouts/gdiRewardTileBlock'), {
    ssr: true
})
const GDIRewardTierTableBlock = loadable(() => import('../gdi/layouts/gdiRewardTierTableBlock'), {
    ssr: true
})
const GDIMapBlock = loadable(() => import('../gdi/assets/gdiMapBlock'), {ssr: false})
const GDISizeGuidesBlock = loadable(() => import('../gdi/layouts/gdiSizeGuidesBlock'), {
    ssr: true
})

const GDI_BLOCKS = {
    GDIImageBlock,
    GDIVideoBlock,
    GDITextBlock,
    GDICtaBlock,
    GDIProductTileBlock,
    GDICarouselGridBlock,
    GDIContentTileBlock,
    GDIHeroSliderBlock,
    GDITableLayoutBlock,
    GDITabsLayoutBlock,
    GDICollapsibleItemBlock,
    GDIContentAssetBlock,
    GDIRewardStatusSectionBlock,
    GDIRewardTileBlock,
    GDIRewardTierTableBlock,
    GDIMapBlock,
    GDISizeGuidesBlock
}

/* CMS payload fields that describe the content node itself rather than its rendering.
 * Spreading these onto a Chakra component leaks them to the DOM as lowercase HTML
 * attributes (e.g. parentlink="[object Object]"). Strip once at the renderer so every
 * GDI block stays clean without each component re-listing them. */
const CMS_METADATA_KEYS = new Set([
    '__typename',
    'ContentLink',
    'ParentLink',
    'Url',
    'RelativePath',
    'SimpleAddress',
    '_sortOrder',
    'Tag',
    'DisplayOption'
])

const stripCmsMetadata = (content) => {
    const out = {}
    for (const key in content) {
        if (CMS_METADATA_KEYS.has(key)) continue
        out[key] = content[key]
    }
    return out
}

const renderGDIBlock = (content, props) => {
    const Block = GDI_BLOCKS[content?.__typename]
    if (Block) {
        const blockProps = stripCmsMetadata(content)
        return <Block {...blockProps} _contentLinkId={content?.ContentLink?.Id} {...props} />
    }
    return <UnsupportedContentFallback content={content} />
}

export const renderPagesSwitch = (content, area) => {
    if (isDebugMode) {
        console.log('📄 DATA LOADED - PAGE - TYPE: ', content.__typename, 'CONTENT: ', content)
    }
    if (content.__typename === 'WebContentPage') {
        return <WebContentPage content={content} />
    }
    if (content.__typename === 'GDIContentPage') {
        return <GDIContentPage content={content} />
    }
    return null
}

export const renderComponentsSwitch = (content, props) => {
    if (isDebugMode) {
        console.log(
            '📄 DATA LOADED - COMPONENT - TYPE: ',
            content?.__typename,
            'CONTENT: ',
            content,
            'PROPS:',
            props
        )
    }
    return renderGDIBlock(content, props)
}

export const renderLayoutsSwitch = (content, props) => {
    if (isDebugMode) {
        console.log('📄 DATA LOADED - LAYOUT - TYPE: ', content?.__typename, 'CONTENT: ', content)
    }
    return renderGDIBlock(content, props)
}

// Legacy carousel-items renderer (VickyProductCarouselBlock etc.). PDP/PLP/cart/loyalty
// recommendation flows will be refactored to GDI components — until then this returns null.
export const renderCarouselItems = () => {
    return null
}
